import {
  dimensionBadgeBase,
  dimensionBadgeStateColors,
} from '@src/components/common/dimensionBadgeStyles';
import { usePublishTrackingStore } from '@src/stores-v2/StoreContext';
import ClockIcon from '@src/svgs/ClockIcon';
import VideoIcon from '@src/svgs/quick-actions/VideoIcon';
import { getAvailableClipFormats } from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { getClipDurationLabel, getClipThumbnailDisplay } from './clipDisplay';
import ClipRowActionsMenu from './ClipRowActionsMenu';
import { selectedRowIndicator } from './selectionStyles';

import type { OtherVideo } from '@src/types.ts/story';
import type { FC } from 'react';

type Props = {
  clip: OtherVideo;
  /**
   * Parent story's frame, shown when the clip has none of its own (an
   * unrendered draft), so the row isn't left with an empty placeholder.
   */
  storyThumbnailUrl?: string;
  /** Whether this clip is the currently highlighted (single-clicked) row. */
  isSelected?: boolean;
  /** Last clip under its story — the tree connector ends in an elbow. */
  isLast?: boolean;
  /** Single-click — highlights the row without navigating. */
  onSelect?: (clip: OtherVideo) => void;
  /** Double-click — opens the clip preview for its parent story. */
  onOpen?: (clip: OtherVideo) => void;
  /** Open the Details panel for this clip (from the row's ⋮ menu). */
  onDetails?: (clip: OtherVideo) => void;
};

const StoryClipRow: FC<Props> = observer(
  ({
    clip,
    storyThumbnailUrl,
    isSelected,
    isLast,
    onSelect,
    onOpen,
    onDetails,
  }) => {
    const publishTrackingStore = usePublishTrackingStore();
    const { url: thumbnail, fit } = getClipThumbnailDisplay(
      clip,
      storyThumbnailUrl,
    );
    const duration = getClipDurationLabel(clip);
    const date = clip._publishedAt
      ? new Date(clip._publishedAt).toLocaleDateString()
      : '';
    // Every aspect ratio the clip holds (e.g. 16:9, 9:16) — not only the
    // published ones. Showing published versions alone left the cell blank for
    // an unrendered clip, which reads as "no data" rather than "nothing
    // published yet"; the badge's state is what carries the difference.
    // Ratio-less variants are still dropped — there is no badge to draw.
    const formats = getAvailableClipFormats(clip).filter(f => f.aspectRatio);

    return (
      <Row
        $selected={isSelected}
        onClick={event => {
          event.stopPropagation();
          onSelect?.(clip);
        }}
        onDoubleClick={() => onOpen?.(clip)}
        data-testid={`story-clip-row-${clip.id}`}
      >
        <Left>
          <Connector $last={!!isLast} aria-hidden="true" />
          <ClipBadge title="Clip" aria-label="Clip">
            <VideoIcon />
          </ClipBadge>
          <Thumb>
            {thumbnail ? (
              <ThumbImg src={thumbnail} $fit={fit} alt="" />
            ) : (
              <ThumbFallback />
            )}
          </Thumb>
          <Title>{clip.title || 'Untitled clip'}</Title>
        </Left>
        <Right data-cols="true">
          <DurationCol title="Duration">
            {duration && (
              <>
                <ClockIcon />
                <span>{duration}</span>
              </>
            )}
          </DurationCol>
          <ContentCol>
            {formats.map(format => {
              // Per aspect ratio, not per clip: each variant is its own Video
              // and its own PublishRecord, so publishing 9:16 must not gild the
              // 16:9 badge. Only covers publishes tracked this session (a fresh
              // fanout, or one seeded for a loaded story) — the dashboard query
              // carries no render state, so an untracked render just shows the
              // variant's last known state.
              const publishing =
                !!format.videoId &&
                publishTrackingStore.isVideoInFlight(format.videoId);
              const state = publishing
                ? 'Publishing…'
                : format.isPublished
                  ? 'Published'
                  : 'Draft';
              return (
                <AspectBadge
                  key={format.aspectRatio}
                  $publishing={publishing}
                  $inactive={!format.isPublished}
                  title={`${format.aspectRatio} · ${state}`}
                >
                  {format.aspectRatio}
                </AspectBadge>
              );
            })}
          </ContentCol>
          <TopicsCol />
          <DateCol>{date}</DateCol>
          {onDetails ? (
            <ClipRowActionsMenu onDetails={() => onDetails(clip)} />
          ) : (
            <ActionsSpacer />
          )}
        </Right>
      </Row>
    );
  },
);

export default StoryClipRow;

// Indent so the connector rail sits under the parent story's thumbnail edge.
// Clips are deliberately NOT column-aligned with story rows: the tree
// connector, smaller thumbnail, and camera badge subordinate them visually.
const CLIP_INDENT = '42px';

const Row = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  padding: 7px var(--spacing-md) 7px ${CLIP_INDENT};
  background: transparent;
  cursor: pointer;
  transition:
    background 120ms ease,
    box-shadow 120ms ease;
  ${({ $selected }) => $selected && selectedRowIndicator}

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'var(--surface-elevated)' : 'var(--surface)'};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
  flex: 1;
`;

// Tree connector linking the clip to its parent story: a vertical rail with a
// horizontal tick into the row; the last clip ends in a rounded elbow (└).
// Rows stack flush, so consecutive rails read as one continuous line.
const Connector = styled.span<{ $last: boolean }>`
  position: relative;
  width: 16px;
  align-self: stretch;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 0;
    bottom: ${({ $last }) => ($last ? '50%' : '0')};
    width: 9px;
    border-left: 1px solid var(--border);
    ${({ $last }) =>
      $last &&
      `
      border-bottom: 1px solid var(--border);
      border-bottom-left-radius: 7px;
    `}
  }

  /* Horizontal tick into the row for non-last clips (the elbow's bottom
     border already provides it for the last one). */
  &::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 50%;
    width: 9px;
    border-top: ${({ $last }) => ($last ? 'none' : '1px solid var(--border)')};
  }
`;

// Marks the row as a clip — sits in the gutter to the left of the thumbnail.
const ClipBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-muted);

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Smaller than the story row's 102x58 thumb so clips read as sub-items. Every
// clip gets this same widescreen box whatever its shape, so the list stays a
// tidy column of identical tiles; the neutral background is what a vertical
// frame is centred against.
const Thumb = styled.div`
  width: 72px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--border);
  flex-shrink: 0;
`;

// Widescreen frames fill the box; a taller frame (9:16, 1:1) is centred whole
// inside it, so a vertical clip shows its real framing instead of a zoomed-in
// crop of its middle.
const ThumbImg = styled.img<{ $fit: 'cover' | 'contain' }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $fit }) => $fit};
`;

const ThumbFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--border) 0%, var(--surface) 100%);
`;

const Title = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  /* Lighter + muted than the story title so clips read as sub-items. */
  font-weight: var(--font-weight-normal);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex-shrink: 0;
`;

const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);

  svg {
    color: var(--text-muted);
  }
`;

// Widths mirror UnfolderedStoryRow's columns so clip values line up under
// their parent story's columns.
const DurationCol = styled(Stat)`
  width: 72px;
  justify-content: flex-start;
`;

// Single "Content" column — holds the published-version aspect-ratio badges,
// width-matched to the header and story/folder rows so they line up under the
// story's Content summary.
const ContentCol = styled.span`
  width: 180px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
`;

// One clip version (e.g. "16:9"), coloured by its publish state from the same
// shared mapping Content Studio's VideoClipCard DimensionBadge uses, so a
// variant reads identically on the card and in this row.
const AspectBadge = styled.span<{
  $publishing?: boolean;
  $inactive?: boolean;
}>`
  ${dimensionBadgeBase}
  display: inline-flex;
  line-height: 1;
  white-space: nowrap;
  ${({ $publishing, $inactive }) =>
    dimensionBadgeStateColors({ publishing: $publishing, inactive: $inactive })}
`;

const TopicsCol = styled(Stat)`
  width: 215px;
  justify-content: flex-start;
`;

const DateCol = styled.span`
  width: 88px;
  text-align: right;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
`;

// Matches StoryListHeader's ActionsSpacer / the story row's kebab-menu column.
const ActionsSpacer = styled.span`
  width: 32px;
  flex-shrink: 0;
`;
